import React, { useEffect } from 'react';
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  useLocation,
  useSearchParams,
} from 'react-router-dom';
import ErrorBoundary from '@components/ErrorBoundary';
import AppLayout from '@components/layout/AppLayout';
import Login from '@components/Login';
import { ResourceDetailPage } from '@resource-kit/ui/react';
import { MenuPlanWeekPage } from '@features/menu-planning';
import { StockLevelsPage } from '@features/inventory';
import { GlobalSearch } from '@features/search';
import { StocktakeListPage } from '@features/stocktake';
import { SuggestionsPage, ReceiveGoodsPage } from '@pages/procurement';
import { PurchaseOrdersPage } from '@features/procurement';
import { StocktakeCountPage, StocktakeReviewPage } from '@pages/stocktake';
import { DispatchDashboard } from '@features/dispatch';
import { OverviewPage } from '@features/overview';
import { APP_ACTION_ROUTES, actionRoutes } from '@/actions';
import { webhookResource, WebhookEventsPage } from '@features/webhooks';
// Sub-PR C Part 2-final — `features/exceptions/` deleted; the
// `/exceptions` route now `<Navigate>`-redirects to the new
// operator-inbox at `/admin/review-queue` (the
// `operator_review_queue` substrate, ADR-0012 §Channel 1).
import {
  ReviewQueuePage,
  WebhookFailuresPage,
  WebhookFailureDetailPage,
  WebhookHealthPage,
} from '@features/admin';
import { AuditOperationsPage, AuditChangesPage } from '@features/audit';
import DocsPage from '@pages/DocsPage';
import Profile from '@pages/Profile';
import { UserDetail, UsersPage } from '@features/users';
import { DeliveryFlowSettingsPage, TenantSettingsPage } from '@features/tenants';
import VerifyEmail from '@pages/VerifyEmail';
import ForgotPassword from '@pages/ForgotPassword';
import ResetPassword from '@pages/ResetPassword';
import { AcceptInvitation } from '@features/auth';
import Unauthorized from '@pages/Unauthorized';
import { useBetterAuth } from './hooks/useAuth';
import { AuthGuard as BetterAuthGuard, GuestGuard, AdminGuard } from '@components/auth/RouteGuard';
import { LandingRedirect } from '@components/auth/LandingRedirect';
import { PermissionGuard } from '@components/auth/PermissionGuard';
import { PERMISSIONS } from './permissions';
import { Analytics } from '@lib/analytics';
import { PrivacyBanner } from '@components/PrivacyBanner';
import { GoogleAnalytics } from '@components/GoogleAnalytics';
import { ToastProvider } from '@components/ui/Toast';
import { ConfirmProvider } from '@components/ui/ConfirmDialog';
import { ReactRouterAdapterProvider } from '@lib/reactRouterAdapter';
import { HgsResourceKitProvider } from '@lib/resourceKitServices';
import { MerchantTimezoneProvider } from '@/contexts/MerchantTimezoneContext';

// Page tracking component
const PageTracker: React.FC = () => {
  const location = useLocation();

  useEffect(() => {
    // Track page view whenever location changes
    Analytics.pageView(location.pathname, document.title);
  }, [location]);

  return null;
};

// Login Route component - now uses BetterAuth
const LoginRoute: React.FC = () => {
  return (
    <GuestGuard>
      <Login />
    </GuestGuard>
  );
};

const RoutePlanningRedirect: React.FC = () => {
  const [sp] = useSearchParams();
  const d = sp.get('date');
  return (
    <Navigate
      to={`/dispatch?tab=route-planning${d ? `&date=${encodeURIComponent(d)}` : ''}`}
      replace
    />
  );
};

/**
 * The bare address of a tab namespace (`/audit`, `/webhooks`) is an ENTRANCE,
 * not a page: it lands on the first tab (#2005). It is also the address the
 * sidebar emits and every pre-#2005 bookmark carries — and these lists keep
 * their filters, sort and page in the QUERY STRING, which the list hydrates
 * from the URL. A bare `<Navigate to="/audit/operations">` forwards the
 * pathname only, so `/audit?actor=<id>` arrived at an UNFILTERED ledger with
 * nothing on screen saying the condition had been dropped (#2186).
 *
 * The hash rides along for the same reason: the entrance must not be the one
 * hop in the app that silently edits the address.
 *
 * A `to` that carries its own query names the parameters the ENTRANCE owns
 * (#2297). They are merged in last, so an inbound value for one of them
 * loses — `/monitoring` exists to reach the route-planning tab, and honouring
 * an inbound `tab` would send the entrance somewhere it is not for. Every
 * other inbound parameter rides along, which a hard-coded destination string
 * cannot do: it replaces the query instead of merging into it. That branch
 * re-serializes the inbound query through `URLSearchParams` rather than
 * forwarding the bytes, so `?a%20b` arrives as `?a+b` and a valueless `?flag`
 * as `?flag=`. Both decode to the same thing for a reader that goes through
 * `URLSearchParams`, which every query reader in this app does — six direct
 * `new URLSearchParams(location.search)` sites and `useSearchParams`
 * elsewhere, with no raw-string reader anywhere. Byte-for-byte preservation
 * is NOT promised on this branch, only on the one where `to` has no query of
 * its own; the decoded values are, and a test pins that.
 */
const NamespaceEntranceRedirect: React.FC<{ to: string }> = ({ to }) => {
  const { search, hash } = useLocation();
  // `indexOf`, not `split('?')`: a `to` holding more than one `?` would make
  // `split` return three pieces and silently drop everything after the second
  // one. No route writes such a `to` today, and this costs one line.
  const mark = to.indexOf('?');
  const own = mark < 0 ? '' : to.slice(mark + 1);
  const pathname = mark < 0 ? to : to.slice(0, mark);
  if (own === '') {
    return <Navigate to={{ pathname, search, hash }} replace />;
  }
  const params = new URLSearchParams(search);
  for (const [key, value] of new URLSearchParams(own)) {
    params.set(key, value);
  }
  return <Navigate to={{ pathname, search: `?${params}`, hash }} replace />;
};

const App: React.FC = () => {
  const { isAuthenticated } = useBetterAuth();

  return (
    <ErrorBoundary>
      <ToastProvider>
        <ConfirmProvider>
          <Router>
            {/* The resource kit reaches the router only through this adapter
                (#1801) — core declares the seams, the host installs them. */}
            <ReactRouterAdapterProvider>
              <GoogleAnalytics />
              <PageTracker />
              <Routes>
                {/* Login route */}
                <Route path="/login" element={<LoginRoute />} />

                {/* Public sign-up is disabled — registration is invitation-only.
                  Invited users land on /accept-invitation from the emailed
                  link and set their own password there. These legacy paths
                  redirect rather than rendering a separate copy of the login
                  screen. */}
                <Route path="/signup" element={<Navigate to="/login" replace />} />
                <Route path="/auth/register" element={<Navigate to="/login" replace />} />
                {/* Invitation accept flow (#1652) — target of INVITE_ACCEPT_URL. */}
                <Route path="/accept-invitation" element={<AcceptInvitation />} />
                {/* Email verification callback route */}
                <Route path="/auth/verify-email" element={<VerifyEmail />} />
                {/* Password reset flow */}
                <Route
                  path="/forgot-password"
                  element={
                    <GuestGuard>
                      <ForgotPassword />
                    </GuestGuard>
                  }
                />
                <Route path="/reset-password" element={<ResetPassword />} />
                <Route path="/unauthorized" element={<Unauthorized />} />

                {/* Protected routes */}
                <Route
                  path="/"
                  element={
                    <BetterAuthGuard>
                      {/* The time zone gate, then the kit's host
                            capabilities — translate / format / notify /
                            confirm / storage / access (#2165) — installed
                            exactly here, INSIDE the gate (#2875). The kit's
                            install check reads `format.timeZone` on every
                            render, and that answer exists only after the gate
                            has the session tenant's zone; the public pages
                            above use no kit seam, so they stay outside both.

                            WHAT IS ON THE SEAM TODAY, and it is not the whole
                            subtree: `packages/resource-kit-ui/**` reads these
                            through useResourceKit() and is forbidden from
                            naming them directly — enforced by
                            `src/__tests__/architecture/resourceKitSeams.test.ts`.
                            Everything ELSE below this provider still calls the
                            host modules itself: the `features/**` pages use
                            `useTranslation` / `useToast` directly. The
                            provider is installed for the kit, not for the
                            tree (ADR-0035 D6, #2169, #2170). */}
                      <MerchantTimezoneProvider>
                        <HgsResourceKitProvider>
                          <AppLayout headerSearch={<GlobalSearch />} />
                        </HgsResourceKitProvider>
                      </MerchantTimezoneProvider>
                    </BetterAuthGuard>
                  }
                >
                  <Route index element={<LandingRedirect />} />
                  {/* Resource routes are DERIVED (#1798): one `defineActions`
                    declaration per resource in `src/actions/catalog.tsx` yields
                    the path, the permission gate, the detail route (or not, for
                    a drawer-only surface) and the sidebar row, so the route
                    table, the menu and the permission catalog can no longer
                    drift apart. `actionRoutes` is the React Router adapter —
                    the description the kit emits is router-neutral.

                    Route guards ask for the key the rebuilt backend
                    actually enforces, not the legacy `:list` split. The
                    commerce read routes gate list AND detail under one
                    `<module>:read` key (commerce http routes), and the
                    delivery drivers routes gate under `dispatch:read`
                    (delivery read-routes). Guarding the list page on a
                    `:list` key the server never checks — and the
                    `/api/permissions` catalog never advertises — would
                    bounce an otherwise-authorized user to /unauthorized.
                    Catalog reconciled against the 25-key backend catalog
                    in #943. */}
                  {actionRoutes(APP_ACTION_ROUTES)}
                  {/* Everything below is HAND-WRITTEN, and ADR-0028 draws the
                    boundary at exactly this line: a NEW permission-gated
                    resource surface belongs in `src/actions/catalog.tsx`, not
                    in a new `<Route>` here. The routes that stay are the ones
                    the action layer deliberately cannot express — an admin
                    ROLE gate (no permission key backs it), an AND/OR of N
                    permissions (an `ActionPermissions` slot holds one key), a
                    third or non-`:id` surface of a record (`ActionViews` holds
                    list + detail), a pure redirect, or a pre-auth page. The
                    set is frozen by name in
                    `src/__tests__/architecture/handwritten-routes-frozen.test.ts`;
                    growing it takes a visible edit to that file. */}
                  {/* The `tasks` table was retired in the backend redesign
                    (#935); the delivery work item is now the fulfillment
                    job. Bookmarks / inbound links to `/tasks` and
                    `/tasks/:id` redirect to the dispatch jobs view. */}
                  <Route path="tasks" element={<Navigate to="/dispatch/jobs" replace />} />
                  <Route path="tasks/:id" element={<Navigate to="/dispatch/jobs" replace />} />
                  {/* Catalog (`catalog/*`) and menu planning (`menu-planning/plans`,
                    `…/:id`) are derived above — re-gated onto `catalog:read`
                    when the backend folded the legacy ingredients / suppliers /
                    dishes / combos / menu_plans keys into the unified
                    `catalog:*` module (#943).

                    The week view is a THIRD surface of the menu-plan record,
                    which a two-surface action (list + detail) has no slot for,
                    so it stays hand-written. */}
                  <Route
                    path="menu-planning/plans/:id/week"
                    element={
                      <PermissionGuard permission={PERMISSIONS.CATALOG_READ}>
                        <MenuPlanWeekPage />
                      </PermissionGuard>
                    }
                  />
                  {/* M4 Inventory (issue #428 — T6). Read-only stock levels table.
                    AND-gate (#465): page fires `/admin/inventory/*`
                    (`inventory:read`) AND `/catalog/ingredients`
                    (`catalog:read`). Re-gated onto the backend's own
                    `inventory:*` and unified `catalog:*` module keys (#943),
                    preserving the two-call AND-gate: a seat holding only
                    one of the two perms would pass a single-permission
                    gate, then watch the other surface 403. See ADR-0005. */}
                  <Route
                    path="inventory/stock"
                    element={
                      <PermissionGuard
                        permissions={[PERMISSIONS.INVENTORY_READ, PERMISSIONS.CATALOG_READ]}
                      >
                        <StockLevelsPage />
                      </PermissionGuard>
                    }
                  />
                  {/* M3 Procurement (issue #611 — M3-T6 per ADR-0018).
                    Three pages: Suggestions (operator-actionable queue),
                    PurchaseOrders (list + detail drawer), ReceiveGoods
                    (tablet-optimised single-PO entry). Re-gated onto the
                    backend's own `procurement:read` / `procurement:write`
                    module keys (#943; formerly reused `ingredients:*`). */}
                  <Route
                    path="procurement/suggestions"
                    element={
                      <PermissionGuard permission={PERMISSIONS.PROCUREMENT_READ}>
                        <SuggestionsPage />
                      </PermissionGuard>
                    }
                  />
                  <Route
                    path="procurement/purchase-orders"
                    element={
                      <PermissionGuard permission={PERMISSIONS.PROCUREMENT_READ}>
                        <PurchaseOrdersPage />
                      </PermissionGuard>
                    }
                  />
                  <Route
                    path="procurement/purchase-orders/:id/receive"
                    element={
                      <PermissionGuard permission={PERMISSIONS.PROCUREMENT_WRITE}>
                        <ReceiveGoodsPage />
                      </PermissionGuard>
                    }
                  />
                  {/* M5 Stocktake (issue #616 — M5-T4 per ADR-0018).
                    Three pages: List (historical + open CTA), Count
                    (tablet-optimised numeric keypad), Review (variance
                    groups + commit confirmation). Re-gated onto the
                    backend's own `stocktake:read` / `stocktake:write`
                    module keys (#943; formerly reused `ingredients:*`). */}
                  <Route
                    path="stocktake"
                    element={
                      <PermissionGuard permission={PERMISSIONS.STOCKTAKE_READ}>
                        <StocktakeListPage />
                      </PermissionGuard>
                    }
                  />
                  <Route
                    path="stocktake/:id/count"
                    element={
                      <PermissionGuard permission={PERMISSIONS.STOCKTAKE_WRITE}>
                        <StocktakeCountPage />
                      </PermissionGuard>
                    }
                  />
                  <Route
                    path="stocktake/:id/review"
                    element={
                      <PermissionGuard permission={PERMISSIONS.STOCKTAKE_WRITE}>
                        <StocktakeReviewPage />
                      </PermissionGuard>
                    }
                  />
                  <Route path="exports" element={<Navigate to="/dispatch/jobs" replace />} />
                  <Route path="profile" element={<Profile />} />
                  <Route path="docs" element={<DocsPage />} />
                  <Route path="overview" element={<OverviewPage />} />
                  {/* Operations (permission-gated) */}
                  {/* The dispatch page hosts overview + route planning,
                    gated on `dispatch:read` alone. It used to also admit
                    `webhook_failures:read` for a monitoring tab folded into
                    this page, but that tab was retired in #2229 (backing
                    endpoint in #2244) with nothing left for a
                    webhook_failures-only holder to see — an unexplained
                    blank page. #2248 narrows the gate so that holder now
                    gets the ordinary permission-denied surface instead. */}
                  <Route
                    path="dispatch"
                    element={
                      <PermissionGuard permission={PERMISSIONS.DISPATCH_READ}>
                        <DispatchDashboard />
                      </PermissionGuard>
                    }
                  />
                  <Route path="dispatch/route-planning" element={<RoutePlanningRedirect />} />
                  {/* `dispatch/jobs` + `dispatch/jobs/:id` are derived above. */}
                  {/* `dispatch/executions` retired in #935 PR-C2: the
                    task-executions surface was dropped with the tasks table on
                    the rebuilt backend, which serves no equivalent route. */}
                  {/* The monitoring tab itself was retired in #2229 (its
                    components deleted with zero remaining production
                    consumers, #2228). The old `/monitoring` path survives as
                    a redirect so bookmarks / inbound links keep working, now
                    aimed at the surviving route-planning tab. The tab is the
                    entrance's own parameter and overrides an inbound one;
                    everything else the link carried is forwarded (#2297) --
                    a hard-coded query string forwards nothing. */}
                  <Route
                    path="monitoring"
                    element={<NamespaceEntranceRedirect to="/dispatch?tab=route-planning" />}
                  />
                  {/* Sub-PR C Part 2-final — /exceptions → /admin/review-queue redirect.
                    The legacy ExceptionsPage component + permission gate are gone.
                    The review queue reads `_tab` off the URL, so this forwards the
                    query string and hash rather than the pathname alone (#2297) --
                    a bare `<Navigate>` kept the bookmark and dropped its state. */}
                  <Route
                    path="exceptions"
                    element={<NamespaceEntranceRedirect to="/admin/review-queue" />}
                  />
                  {/* Admin only */}
                  <Route
                    path="webhooks"
                    element={<NamespaceEntranceRedirect to="/webhooks/health" />}
                  />
                  <Route
                    path="webhooks/events"
                    element={
                      <AdminGuard>
                        <WebhookEventsPage />
                      </AdminGuard>
                    }
                  />
                  <Route
                    path="webhooks/events/:id"
                    element={
                      <AdminGuard>
                        <ResourceDetailPage bundle={webhookResource} />
                      </AdminGuard>
                    }
                  />
                  <Route
                    path="webhooks/failures"
                    element={
                      <AdminGuard>
                        <WebhookFailuresPage />
                      </AdminGuard>
                    }
                  />
                  <Route
                    path="webhooks/failures/:id"
                    element={
                      <AdminGuard>
                        <WebhookFailureDetailPage />
                      </AdminGuard>
                    }
                  />
                  <Route
                    path="webhooks/health"
                    element={
                      <AdminGuard>
                        <WebhookHealthPage />
                      </AdminGuard>
                    }
                  />
                  <Route
                    path="users"
                    element={
                      <AdminGuard>
                        <UsersPage />
                      </AdminGuard>
                    }
                  />
                  <Route
                    path="users/:id"
                    element={
                      <AdminGuard>
                        <UserDetail />
                      </AdminGuard>
                    }
                  />
                  {/* Tenant admin pages (#2366): admin-ROLE gated, like users. */}
                  <Route
                    path="settings/tenant"
                    element={
                      <AdminGuard>
                        <TenantSettingsPage />
                      </AdminGuard>
                    }
                  />
                  <Route
                    path="settings/delivery-flow"
                    element={
                      <AdminGuard>
                        <DeliveryFlowSettingsPage />
                      </AdminGuard>
                    }
                  />
                  {/* Sub-PR F (issue #516): operator-actionable inbox for
                    `operator_review_queue` rows (ADR-0012 §Channel 1).
                    Backend route is admin-only; AdminGuard mirrors that
                    server-side gate. */}
                  <Route
                    path="admin/review-queue"
                    element={
                      <AdminGuard>
                        <ReviewQueuePage />
                      </AdminGuard>
                    }
                  />
                  {/* The webhook failure inbox, its detail view and the health
                    panel moved under `/webhooks/*` in #2005 — see above. The
                    old `/admin/webhook-*` addresses are DELETED, not
                    redirected (owner decision, 2026-08-08): this is an internal
                    console whose links live in the sidebar, and a redirect kept
                    forever is a second name for one page that nobody prunes. */}
                  {/* The two audit ledgers (#1981): `/audit` is the REQUEST
                    ledger, `/audit/changes` the DATA-CHANGE ledger. Sibling
                    routes sharing a tab strip, not a nested layout. Both are
                    admin-ROLE gated (`AdminGuard`, mirroring the backend), so
                    they stay hand-written per ADR-0028 — `defineActions`
                    requires a permission key and no key backs the admin role. */}
                  {/* #2005: each tab under its own namespace. The bare `/audit`
                    is a redirect to the first tab, not a fourth page — it is
                    the sidebar's entrance, and it must not become a second
                    address for the same view. */}
                  <Route
                    path="audit"
                    element={<NamespaceEntranceRedirect to="/audit/operations" />}
                  />
                  <Route
                    path="audit/operations"
                    element={
                      <AdminGuard>
                        <AuditOperationsPage />
                      </AdminGuard>
                    }
                  />
                  <Route
                    path="audit/changes"
                    element={
                      <AdminGuard>
                        <AuditChangesPage />
                      </AdminGuard>
                    }
                  />
                </Route>

                {/* Catch-all route */}
                <Route
                  path="*"
                  element={isAuthenticated ? <LandingRedirect /> : <Navigate to="/login" replace />}
                />
              </Routes>

              {/* Privacy banner for GDPR compliance */}
              <PrivacyBanner />
            </ReactRouterAdapterProvider>
          </Router>
        </ConfirmProvider>
      </ToastProvider>
    </ErrorBoundary>
  );
};

export default App;
